import { useRef } from 'react';
import { Typography, Stack, FormControl, Link as MuiLink } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuthState } from 'state/useAuthState';
import {
  PlanCard,
  CardContent,
  ExpandedContent,
} from 'features/membership/components/PlanCard';
import { useMembershipPlan } from 'features/membership/components/MembershipPlanProvider';
import { useCenterPlanCard } from 'features/membership/hooks/useCenterPlanCard';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import {
  MEMBERSHIP_PLANS,
  MEMBERSHIP_PLANS_AMOUNTS,
} from 'features/membership/membership.constants';
import { usePostHog } from 'posthog-js/react';
import { useSupportUrlState } from 'features/membership/hooks/useSupportUrlState';
import { Loader } from 'components/Loader';

export const SupportUsContent = (): JSX.Element => {
  const { t } = useTranslation();
  const { plan: selectedPlan, source, setPlan } = useSupportUrlState();
  const { verifyingToken } = useMembershipPlan();
  const { isAuthenticated } = useAuthState();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const posthog = usePostHog();

  useCenterPlanCard({
    containerRef,
    selectedPlan,
  });

  if (verifyingToken) {
    return <Loader disableBackground />;
  }

  return (
    <SimplePageLayout
      maxWidth="sm"
      ref={containerRef}
      bgcolor="background.membership"
      containerSx={{
        /*
         * Reserves space for the scrollbar on both sides to prevent layout shifts
         * in centered designs when content expands.
         * Supported by all modern browsers.
         */
        scrollbarGutter: 'stable both-edges',
      }}
    >
      <Stack spacing={4} sx={{ alignItems: 'center' }}>
        <Typography>
          <Trans i18nKey="supportUs.description">
            As a non-profit, all critical safety features will be
            <em>free</em>, <b>forever</b>. Your financial contribution helps
            ensure free access for all.
          </Trans>
        </Typography>

        <FormControl fullWidth>
          <Stack
            spacing={3}
            role="radiogroup"
            aria-label={t('supportUs.supportPlans')}
          >
            <PlanCard
              name={t('supportUs.membership.title')}
              value={MEMBERSHIP_PLANS.yearly}
              onChange={(value) => {
                posthog.capture('toggled_membership_plan', {
                  plan: value,
                  source,
                });
                setPlan(value);
              }}
              checked={selectedPlan === MEMBERSHIP_PLANS.yearly}
              buttonTitle={t('supportUs.membership.ctaButton')}
            >
              <PlanCard.Content>
                <CardContent
                  title={t('supportUs.membership.title')}
                  plan={MEMBERSHIP_PLANS.yearly}
                  description={t('supportUs.membership.description')}
                  amount={MEMBERSHIP_PLANS_AMOUNTS[MEMBERSHIP_PLANS.yearly]}
                />
              </PlanCard.Content>
              <PlanCard.CollapseContent>
                <ExpandedContent plan={MEMBERSHIP_PLANS.yearly} />
              </PlanCard.CollapseContent>
            </PlanCard>

            <PlanCard
              name={t('supportUs.proMembership.title')}
              value={MEMBERSHIP_PLANS.proYearly}
              onChange={(value) => {
                posthog.capture('toggled_membership_plan', {
                  plan: value,
                  source,
                });
                setPlan(value);
              }}
              checked={
                selectedPlan === MEMBERSHIP_PLANS.proYearly ||
                selectedPlan === MEMBERSHIP_PLANS.proMonthly
              }
              buttonTitle={t('supportUs.proMembership.ctaButton')}
            >
              <PlanCard.Content>
                <CardContent
                  title={t('supportUs.proMembership.title')}
                  plan={MEMBERSHIP_PLANS.proYearly}
                  description={t('supportUs.proMembership.description')}
                  amount={MEMBERSHIP_PLANS_AMOUNTS[MEMBERSHIP_PLANS.proYearly]}
                />
              </PlanCard.Content>
              <PlanCard.CollapseContent>
                <ExpandedContent plan={MEMBERSHIP_PLANS.proYearly} />
              </PlanCard.CollapseContent>
            </PlanCard>

            <PlanCard
              name={t('supportUs.donate.title')}
              value="donate"
              onChange={(value) => {
                posthog.capture('toggled_membership_plan', {
                  plan: value,
                  source,
                });
                setPlan(value);
              }}
              checked={selectedPlan === 'donate'}
              buttonTitle={t('supportUs.donate.ctaButton')}
            >
              <PlanCard.Content>
                <CardContent
                  title={t('supportUs.donate.title')}
                  description={t('supportUs.donate.description')}
                />
              </PlanCard.Content>
              <PlanCard.CollapseContent>
                <ExpandedContent />
              </PlanCard.CollapseContent>
            </PlanCard>

            <PlanCard
              name={t('supportUs.teams.title')}
              value="teams"
              onChange={(value) => {
                posthog.capture('toggled_membership_plan', {
                  plan: value,
                  source,
                });
                setPlan(value);
              }}
              checked={selectedPlan === 'teams'}
              buttonTitle={t('supportUs.teams.ctaButton')}
            >
              <PlanCard.Content>
                <CardContent
                  title={t('supportUs.teams.title')}
                  description={t('supportUs.teams.description')}
                />
              </PlanCard.Content>
              <PlanCard.CollapseContent>
                <ExpandedContent plan="teams" />
              </PlanCard.CollapseContent>
            </PlanCard>
          </Stack>
        </FormControl>

        {!isAuthenticated && (
          <Typography variant="body1">
            <Trans i18nKey="supportUs.alreadyAMember">
              Already have an account?
              <MuiLink
                component={Link}
                to="/login"
                color="inherit"
                fontWeight={500}
              >
                Log In
              </MuiLink>
            </Trans>
          </Typography>
        )}
      </Stack>
    </SimplePageLayout>
  );
};
